import React, { useState } from 'react';
import { X, Cable, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { BackboneLink, LocationData } from '../types';
import { BACKBONE_FIBER_TYPES, getLocationDistanceKm } from '../constants';

interface BackboneModalProps {
  onClose: () => void;
  locations: Record<string, LocationData>;
  backboneLinks: BackboneLink[];
  money: number;
  onInstall: (fromLocation: string, toLocation: string, fiberTypeId: string) => void;
  onUpgrade: (linkId: string, newFiberTypeId: string, upgradeCost: number) => void;
  onRemove: (linkId: string) => void;
  backboneCostPerDay: number;
  formatCurrency: (amount: number, decimals?: number) => string;
}

function locationPairKey(a: string, b: string) {
  return [a, b].sort().join('|');
}

function getDistance(a: string, b: string): number {
  return getLocationDistanceKm(a, b);
}

const TIER_COLORS: Record<string, string> = {
  '10g': 'text-blue-400',
  '100g': 'text-indigo-400',
  '400g': 'text-violet-400',
};

const BackboneModal: React.FC<BackboneModalProps> = ({
  onClose,
  locations,
  backboneLinks,
  money,
  onInstall,
  onUpgrade,
  onRemove,
  backboneCostPerDay,
  formatCurrency,
}) => {
  const unlockedLocations = Object.entries(locations)
    .filter(([, loc]) => loc.unlocked)
    .map(([id, loc]) => ({ id, name: loc.name }));

  const [fromLoc, setFromLoc] = useState(unlockedLocations[0]?.id ?? '');
  const [toLoc, setToLoc] = useState(unlockedLocations[1]?.id ?? '');
  const [fiberTypeId, setFiberTypeId] = useState(BACKBONE_FIBER_TYPES[0].id);

  const selectedFiber = BACKBONE_FIBER_TYPES.find(f => f.id === fiberTypeId)!;
  const existingLink = backboneLinks.find(l =>
    locationPairKey(l.fromLocation, l.toLocation) === locationPairKey(fromLoc, toLoc)
  );
  const existingFiber = existingLink ? BACKBONE_FIBER_TYPES.find(f => f.id === existingLink.fiberTypeId) : null;
  const isUpgrade = !!existingLink && selectedFiber.capacityGbps > (existingFiber?.capacityGbps ?? 0);
  const upgradeCost = isUpgrade ? Math.max(0, selectedFiber.costPerLink - (existingFiber?.costPerLink ?? 0)) : 0;

  const canInstall = fromLoc && toLoc && fromLoc !== toLoc && !existingLink && money >= selectedFiber.costPerLink;
  const canUpgrade = isUpgrade && money >= upgradeCost;

  const totalCapacity = backboneLinks.reduce((acc, link) => {
    const ft = BACKBONE_FIBER_TYPES.find(f => f.id === link.fiberTypeId);
    return acc + (ft?.capacityGbps ?? 0);
  }, 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <Cable className="w-5 h-5 text-violet-400" />
            <h2 className="text-lg font-bold text-white">Backbone / Dark Fiber</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Active links</div>
              <div className="text-xl font-bold text-white">{backboneLinks.length}</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Total capacity</div>
              <div className="text-xl font-bold text-violet-400">{totalCapacity}G</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Monthly cost</div>
              <div className="text-xl font-bold text-red-400">{formatCurrency(backboneCostPerDay * 30)}/mo</div>
            </div>
          </div>

          {/* Install new link */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-3">Install New Link</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">From</label>
                <select
                  value={fromLoc}
                  onChange={e => setFromLoc(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white"
                >
                  {unlockedLocations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">To</label>
                <select
                  value={toLoc}
                  onChange={e => setToLoc(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-sm text-white"
                >
                  {unlockedLocations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {fromLoc && toLoc && fromLoc !== toLoc && (
              <div className="text-xs text-gray-400 mb-3">
                Distance: ~{getDistance(fromLoc, toLoc)} km
              </div>
            )}

            {/* Fiber type selector */}
            <div className="space-y-2 mb-4">
              {BACKBONE_FIBER_TYPES.map(ft => (
                <label
                  key={ft.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    fiberTypeId === ft.id ? 'border-violet-500 bg-violet-900/20' : 'border-gray-700 hover:border-gray-500'
                  }`}
                >
                  <input type="radio" name="fiber" value={ft.id} checked={fiberTypeId === ft.id} onChange={() => setFiberTypeId(ft.id)} className="sr-only" />
                  <div className="flex-1">
                    <div className={`font-medium ${TIER_COLORS[ft.id]}`}>{ft.name}</div>
                    <div className="text-xs text-gray-400">{ft.description}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-white">{formatCurrency(ft.costPerLink)}</div>
                    <div className="text-xs text-gray-400">{formatCurrency(ft.monthlyFeePerLink)}/mo</div>
                  </div>
                  <div className={`text-sm font-bold shrink-0 ${TIER_COLORS[ft.id]}`}>{ft.capacityGbps}G</div>
                </label>
              ))}
            </div>

            {existingLink && !isUpgrade && selectedFiber.id !== existingLink.fiberTypeId && (
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-3">
                <AlertTriangle size={12} /> Downgrade not available — select a higher tier to upgrade.
              </div>
            )}
            {existingLink && selectedFiber.id === existingLink.fiberTypeId && (
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-3">
                <CheckCircle size={12} className="text-emerald-500" /> This tier is already installed on this route.
              </div>
            )}
            {fromLoc === toLoc && (
              <div className="text-xs text-gray-500 mb-3">Select two different locations.</div>
            )}

            {isUpgrade ? (
              <button
                disabled={!canUpgrade}
                onClick={() => { if (canUpgrade && existingLink) { onUpgrade(existingLink.id, fiberTypeId, upgradeCost); } }}
                className={`w-full py-2 rounded-lg font-medium text-sm transition-colors ${
                  canUpgrade ? 'bg-violet-600 hover:bg-violet-500 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {money < upgradeCost
                  ? `Need ${formatCurrency(upgradeCost - money)} more to upgrade`
                  : `Upgrade to ${selectedFiber.name} — ${formatCurrency(upgradeCost)}`}
              </button>
            ) : (
              <button
                disabled={!canInstall}
                onClick={() => { if (canInstall) { onInstall(fromLoc, toLoc, fiberTypeId); } }}
                className={`w-full py-2 rounded-lg font-medium text-sm transition-colors ${
                  canInstall ? 'bg-violet-600 hover:bg-violet-500 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {existingLink
                  ? 'Select a different tier to upgrade'
                  : money < selectedFiber.costPerLink
                    ? `Need ${formatCurrency(selectedFiber.costPerLink - money)} more`
                    : `Install ${selectedFiber.name} — ${formatCurrency(selectedFiber.costPerLink)}`}
              </button>
            )}
          </div>

          {/* Active links */}
          {backboneLinks.length > 0 && (
            <div>
              <h3 className="font-semibold text-white mb-3">Active Links</h3>
              <div className="space-y-2">
                {backboneLinks.map(link => {
                  const ft = BACKBONE_FIBER_TYPES.find(f => f.id === link.fiberTypeId);
                  const fromName = locations[link.fromLocation]?.name ?? link.fromLocation;
                  const toName = locations[link.toLocation]?.name ?? link.toLocation;
                  return (
                    <div key={link.id} className="flex items-center gap-3 bg-gray-800 rounded-lg px-4 py-3">
                      <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm text-white">{fromName} ↔ {toName}</div>
                        <div className={`text-xs ${TIER_COLORS[link.fiberTypeId] ?? 'text-gray-400'}`}>
                          {ft?.name} · {ft?.capacityGbps}G · {formatCurrency(ft?.monthlyFeePerLink ?? 0)}/mo
                        </div>
                      </div>
                      <button
                        onClick={() => onRemove(link.id)}
                        className="text-gray-500 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* How it works */}
          <div className="bg-gray-800/50 rounded-lg p-4 text-xs text-gray-400 space-y-1">
            <div className="font-semibold text-gray-300 mb-2">How backbone links work</div>
            <div>• Traffic between linked DCs routes over your private fiber instead of transit providers.</div>
            <div>• Reduces transit costs proportional to how much traffic fits on the backbone.</div>
            <div>• Backbone capacity shared across all servers at both endpoints.</div>
            <div>• Monthly fee replaces per-Gbps transit cost for covered traffic.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackboneModal;
